import React, { useState } from "react";
import {
  AlertCircle,
  ArrowRight,
  BadgeCheck,
  Check,
  ChevronRight,
  CreditCard,
  LockKeyhole,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  X,
} from "lucide-react";
import { bankTransferAutoConfirmApiService } from "../../../../infrastructure/api/company/bank-transfer-auto-confirm-api-service";
import type {
  AutoConfirmPlan,
  BankAccountType,
  PrepareAutoConfirmCheckout,
} from "../../../../domain/types/bank-transfer-auto-confirm";
import { BankType } from "../../../../domain/types/company-bank-transfer";
import {
  BANKS,
  BankMark,
  Field,
  ModalShell,
  StepTitle,
  inputClass,
  isUserCanceled,
  loadTossSdk,
  requiresIdentity,
  requiresWeb,
  requiresWebPassword,
} from "./shared";
import { AutoConfirmTermsModal } from "./terms";
import { money } from "../bankTransferFormat";

export const PurchaseWizard = ({
  companyId,
  hasBillingMethod,
  onClose,
  onCompleted,
}: {
  companyId: number;
  hasBillingMethod: boolean;
  onClose: () => void;
  onCompleted: () => void;
}) => {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  /** 다른 창이 먼저 끝냈다. 다시 눌러 봐야 소용없으므로 결제 버튼을 닫는다 */
  const [stale, setStale] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [form, setForm] = useState<PrepareAutoConfirmCheckout>({
    bank: "KB_KOOKMIN",
    bankAccountType: "CORPORATE",
    accountNumber: "",
    accountHolder: "",
    bankAccountPassword: "",
    webId: "",
    webPassword: "",
    identityNumber: "",
    plan: "MONTHLY",
    agreedToTerms: false,
  });
  const bankLabel = BANKS.find((item) => item.value === form.bank)?.label;
  const amount = form.plan === "MONTHLY" ? 10_000 : 100_000;
  const needsIdentity =
    requiresIdentity.has(form.bank) ||
    (form.bank === "K_BANK" && form.bankAccountType === "CORPORATE");
  const identityLength = form.bankAccountType === "CORPORATE" ? 10 : 6;
  const identityLabel =
    form.bankAccountType === "CORPORATE" ? "사업자번호" : "주민등록번호 앞 6자리";
  const canNext =
    form.accountNumber.length >= 8 &&
    form.accountHolder.trim() &&
    form.bankAccountPassword.trim() &&
    (form.bank !== "K_BANK" || form.bankAccountType === "CORPORATE") &&
    (!requiresWeb.has(form.bank) || form.webId?.trim()) &&
    (!requiresWebPassword.has(form.bank) || form.webPassword?.trim()) &&
    (!needsIdentity || form.identityNumber?.length === identityLength);

  const submit = async () => {
    // 버튼이 잠기지만 엔터로도 들어온다. 두 번 들어오면 결제 요청이 두 개 만들어지고,
    // 서버가 뒤에서 하나를 떨궈도 사장님 눈에는 "왜 실패했지"만 남는다.
    if (submitting) return;
    setSubmitting(true);
    setError("");
    try {
      const prepared = await bankTransferAutoConfirmApiService.prepare(
        companyId,
        form,
      );
      if (prepared.needsBillingAuth) {
        await loadTossSdk();
        if (!window.TossPayments) throw new Error("결제창을 열지 못했습니다. 잠시 후 다시 시도해 주세요.");
        const payment = window
          .TossPayments(prepared.clientKey)
          .payment({ customerKey: prepared.customerKey });
        await payment.requestBillingAuth({
          method: "CARD",
          successUrl: prepared.successUrl,
          failUrl: prepared.failUrl,
          customerName: form.accountHolder,
        });
        return;
      }
      await bankTransferAutoConfirmApiService.activate(
        companyId,
        prepared.checkoutKey,
        { customerKey: prepared.customerKey },
      );
      await onCompleted();
    } catch (e) {
      if (isUserCanceled(e)) return;
      const message = e instanceof Error ? e.message : "신청을 완료하지 못했습니다.";
      setError(message);
      // 다른 창에서 같은 계좌를 이미 등록했다는 뜻이다. 여기서 다시 눌러도 같은 답만 온다 —
      // 목록을 새로 받아 등록된 계좌를 보여주는 편이 낫다.
      // 여기서 곧바로 닫으면 사장님은 창이 사라진 이유를 못 본다.
      // 오류는 그대로 두고, 나갈 때 목록을 새로 받게 한다.
      if (message.includes("이미 자동확인을 이용 중이거나")) setStale(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell onClose={onClose}>
      <div className="w-full max-w-[900px] overflow-hidden rounded-[12px] bg-white shadow-[0_20px_60px_rgba(6,18,44,0.20)]">
        <div className="border-b border-[#e8eaf0] px-5 py-5 sm:px-7">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#EBF3FF] text-[#4285F4]">
                <CreditCard size={18} />
              </span>
              <div>
                <div className="text-lg font-bold tracking-[-0.4px] text-[#222222]">
                  계좌 자동확인 시작하기
                </div>
                <div className="mt-1 text-xs text-[#8990a0]">
                  결제가 완료되면 바로 자동확인이 시작됩니다.
                </div>
              </div>
            </div>
            <button
              aria-label="닫기"
              onClick={onClose}
              className="rounded-lg p-2 text-[#858c9b] transition hover:bg-gray-100"
            >
              <X size={20} />
            </button>
          </div>
          <div className="mt-6 grid grid-cols-4">
            {["은행 선택", "계좌 연결", "플랜 선택", "결제"].map((label, index) => {
              const number = index + 1;
              const active = step === number;
              const done = step > number;
              return (
                <div key={label} className="relative flex flex-col items-center gap-2">
                  {index > 0 && (
                    <span
                      className={`absolute right-1/2 top-3 h-px w-full ${
                        done || active ? "bg-[#4285F4]" : "bg-[#e2e5eb]"
                      }`}
                    />
                  )}
                  <span
                    className={`relative z-10 flex h-7 w-7 items-center justify-center rounded-full border text-[11px] font-bold ${
                      active || done
                        ? "border-[#4285F4] bg-[#4285F4] text-white"
                        : "border-[#dfe3e9] bg-white text-[#969dac]"
                    }`}
                  >
                    {done ? <Check size={13} /> : number}
                  </span>
                  <span
                    className={`text-[10px] font-bold sm:text-xs ${
                      active ? "text-[#4285F4]" : "text-[#8991a1]"
                    }`}
                  >
                    {label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="max-h-[68vh] min-h-[410px] overflow-y-auto px-5 py-6 sm:px-8">
          {error && (
            <div className="mb-5 flex items-center gap-2 rounded-lg bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700">
              <AlertCircle size={16} /> {error}
            </div>
          )}

          {step === 1 && (
            <div>
              <StepTitle
                number="01"
                title="어느 은행 계좌를 연결할까요?"
                description="자동으로 입금 내역을 확인할 계좌의 은행을 선택해 주세요."
              />
              <div className="mt-6 grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-5">
                {BANKS.map((bank) => {
                  const selected = form.bank === bank.value;
                  return (
                    <button
                      key={bank.value}
                      type="button"
                      onClick={() =>
                        setForm({
                          ...form,
                          bank: bank.value,
                          bankAccountType:
                            bank.value === "K_BANK"
                              ? "CORPORATE"
                              : form.bankAccountType,
                          webId: "",
                          webPassword: "",
                          identityNumber: "",
                        })
                      }
                      className={`relative flex min-h-[88px] flex-col items-center justify-center gap-2 rounded-[10px] border p-3 transition ${
                        selected
                          ? "border-[#4285F4] bg-[#F7FAFF] ring-2 ring-[#4285F4]/10"
                          : "border-[#e0e4eb] bg-white hover:border-[#9EBEF4] hover:bg-[#FAFCFF]"
                      }`}
                    >
                      <BankMark bank={bank.value} small />
                      <span className="text-[11px] font-bold text-[#3a4150]">
                        {bank.label.replace("은행", "")}
                      </span>
                      {selected && (
                        <span className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-[#4285F4] text-white">
                          <Check size={11} />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              <div className="mt-5 flex items-center gap-3 rounded-[10px] bg-[#F7FAFF] px-4 py-3.5 text-xs text-[#63708a]">
                <ShieldCheck className="shrink-0 text-[#4285F4]" size={18} />
                <span>
                  <b className="text-[#3f4d68]">안전한 자동 조회</b> · 입력하신 정보는 암호화해
                  보관하며, 입금내역을 조회할 때만 씁니다.
                </span>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="mx-auto max-w-[720px]">
              <StepTitle
                number="02"
                title={`${bankLabel} 계좌를 연결해 주세요`}
                description="입금 내역을 안전하게 확인하는 데 필요한 정보입니다."
              />
              <div className="mt-6 flex items-center gap-3 rounded-[10px] border border-[#e1e5eb] bg-[#fafbfc] p-4">
                <BankMark bank={form.bank} />
                <div className="flex-1">
                  <div className="text-[10px] font-bold text-[#9aa1af]">선택한 은행</div>
                  <div className="mt-1 text-sm font-bold text-[#303744]">{bankLabel}</div>
                </div>
                <button
                  onClick={() => setStep(1)}
                  className="rounded-lg border border-[#dfe3e9] bg-white px-3 py-2 text-xs font-bold text-[#5f6878]"
                >
                  변경
                </button>
              </div>
              <div className="mt-6 grid gap-5 sm:grid-cols-2">
                <Field label="계좌번호">
                  <input
                    value={form.accountNumber}
                    onChange={(e) =>
                      setForm({ ...form, accountNumber: e.target.value.replace(/\D/g, "") })
                    }
                    placeholder="숫자만 입력"
                    className={inputClass}
                  />
                </Field>
                <Field label="예금주명">
                  <input
                    value={form.accountHolder}
                    onChange={(e) => setForm({ ...form, accountHolder: e.target.value })}
                    placeholder="사업자 또는 예금주명"
                    className={inputClass}
                  />
                </Field>
                <Field label="계좌 유형">
                  <select
                    value={form.bankAccountType}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        bankAccountType: e.target.value as "CORPORATE" | "PERSONAL",
                        identityNumber: "",
                      })
                    }
                    className={inputClass}
                  >
                    <option value="CORPORATE">법인계좌</option>
                    <option value="PERSONAL" disabled={form.bank === "K_BANK"}>
                      개인계좌
                    </option>
                  </select>
                  {form.bank === "K_BANK" && (
                    <span className="mt-2 block text-[11px] font-semibold leading-5 text-amber-700">
                      케이뱅크 개인계좌는 빠른조회 서비스를 지원하지 않아 법인계좌만
                      연결할 수 있습니다.
                    </span>
                  )}
                </Field>
                <Field label="계좌 비밀번호">
                  <input
                    type="password"
                    maxLength={8}
                    value={form.bankAccountPassword}
                    onChange={(e) =>
                      setForm({ ...form, bankAccountPassword: e.target.value })
                    }
                    placeholder="은행 계좌 비밀번호"
                    className={inputClass}
                  />
                </Field>
                {requiresWeb.has(form.bank) && (
                  <>
                  <Field label="빠른 조회 아이디">
                    <input
                      value={form.webId}
                      onChange={(e) => setForm({ ...form, webId: e.target.value })}
                      placeholder="인터넷뱅킹 아이디"
                      className={inputClass}
                    />
                  </Field>
                  {requiresWebPassword.has(form.bank) && (
                    <Field label="빠른 조회 비밀번호">
                      <input
                        type="password"
                        value={form.webPassword}
                        onChange={(e) =>
                          setForm({ ...form, webPassword: e.target.value })
                        }
                        className={inputClass}
                      />
                    </Field>
                  )}
                  </>
                )}
                {needsIdentity && (
                  <Field label={identityLabel}>
                    <input
                      value={form.identityNumber}
                      maxLength={identityLength}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          identityNumber: e.target.value.replace(/\D/g, ""),
                        })
                      }
                      placeholder={`${identityLength}자리 숫자`}
                      className={inputClass}
                    />
                  </Field>
                )}
              </div>
              <div className="mt-6 flex items-start gap-3 rounded-[10px] border border-[#DDE9FC] bg-[#F7FAFF] p-4">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-[#4285F4]">
                  <LockKeyhole size={17} />
                </span>
                <div>
                  <div className="text-xs font-bold text-[#3f4b62]">입력하신 정보는 안전하게 보관됩니다</div>
                  <p className="mt-1 text-[11px] leading-5 text-[#778198]">
                    암호화해 보관하며 입금내역을 조회할 때만 씁니다. 저장된 값은 저희도 볼 수 없습니다.
                  </p>
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <StepTitle
                number="03"
                title="내게 맞는 플랜을 선택하세요"
                description="두 플랜 모두 동일한 자동확인 기능을 제공합니다."
              />
              <div className="mt-7 grid gap-5 sm:grid-cols-2">
              <PlanCard
                selected={form.plan === "MONTHLY"}
                title="월간 플랜"
                price="10,000원"
                detail="부담 없이 매월 자동결제"
                onClick={() => setForm({ ...form, plan: "MONTHLY" })}
              />
              <PlanCard
                selected={form.plan === "ANNUAL"}
                title="연간 플랜"
                price="100,000원"
                detail="월 환산 8,333원 · 2개월 절약"
                badge="가장 인기"
                onClick={() => setForm({ ...form, plan: "ANNUAL" })}
              />
              </div>
              <div className="mt-5 flex items-center gap-2 rounded-xl bg-[#f7f8fa] p-4 text-xs leading-5 text-[#687083]">
                <RefreshCw size={15} className="shrink-0 text-[#4285F4]" />
                해지해도 결제한 이용기간 마지막 날까지 자동확인을 계속 이용할 수 있습니다.
              </div>
            </div>
          )}

          {step === 4 && (
            <div>
              <StepTitle
                number="04"
                title="마지막으로 결제 정보를 확인해 주세요"
                description="결제가 완료되면 계좌 등록과 자동확인이 바로 이어집니다."
              />
              <div className="mt-7 grid gap-5 lg:grid-cols-[1.25fr_0.75fr]">
                <div className="space-y-4">
                  <section className="rounded-[10px] border border-[#e1e5ec] p-5">
                    <div className="mb-4 flex items-center justify-between">
                      <h3 className="text-sm font-bold text-[#303744]">구독 정보</h3>
                      <button
                        onClick={() => setStep(3)}
                        className="text-xs font-bold text-[#4285F4]"
                      >
                        변경
                      </button>
                    </div>
                    <div className="flex items-center gap-3 rounded-xl bg-[#f7f8fa] p-4">
                      <BankMark bank={form.bank} />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-bold text-[#303744]">
                          {bankLabel} 자동확인
                        </div>
                        <div className="mt-1 truncate text-[11px] text-[#8a92a2]">
                          {form.accountNumber} · {form.plan === "MONTHLY" ? "월간" : "연간"} 플랜
                        </div>
                      </div>
                      <strong className="text-sm font-bold text-[#303744]">{money(amount)}</strong>
                    </div>
                  </section>
                  <section className="rounded-[10px] border border-[#e1e5ec] p-5">
                    <h3 className="text-sm font-bold text-[#303744]">결제 수단</h3>
                    <div className="mt-4 flex items-center gap-3 rounded-xl border border-[#cad7f3] bg-[#f6f8ff] p-4">
                      <span className="flex h-9 w-12 items-center justify-center rounded-lg bg-white text-[10px] font-bold text-[#3367D6]">
                        CARD
                      </span>
                      <div className="flex-1 text-xs font-bold text-[#465069]">
                        {hasBillingMethod ? "등록된 정기결제 카드" : "토스페이먼츠 카드 인증"}
                      </div>
                      <BadgeCheck size={18} className="text-[#4285F4]" />
                    </div>
                  </section>
                </div>
                <aside className="rounded-[10px] border border-[#DDE9FC] bg-[#F7FAFF] p-5 text-[#222222]">
                  <h3 className="text-sm font-bold">결제 요약</h3>
                  <SummaryRow
                    label={`${bankLabel} 자동확인`}
                    value={money(amount)}
                  />
                  <SummaryRow label="부가세" value="포함" />
                  {form.plan === "ANNUAL" && (
                    <div className="mt-3 rounded-lg bg-[#EBF3FF] px-3 py-2 text-[11px] font-bold text-[#4285F4]">
                      연간 플랜으로 2개월분 절약
                    </div>
                  )}
                  <div className="mt-5 border-t border-[#DDE4EE] pt-5">
                    <div className="text-xs text-[#777777]">오늘 결제 금액</div>
                    <div className="mt-2 text-2xl font-bold tracking-[-0.5px] text-[#4285F4]">{money(amount)}</div>
                  </div>
                  <div className="mt-5 flex items-center gap-1.5 text-[10px] text-[#777777]">
                    <ShieldCheck size={13} /> 안전한 암호화 결제
                  </div>
                </aside>
              </div>

              <section className="mt-5 rounded-[10px] border border-[#e1e5ec] bg-[#fbfcfd] p-5">
                <h3 className="text-sm font-bold text-[#303744]">결제 전 확인해 주세요</h3>
                <ul className="mt-3 space-y-2 text-[12px] leading-relaxed text-[#5a6272]">
                  <li>
                    · 계좌 1건당 <strong className="font-bold text-[#303744]">{money(amount)}(부가세 포함)</strong>이
                    {form.plan === "MONTHLY" ? " 매월 " : " 매년 "}
                    등록하신 카드로 자동 결제됩니다.
                  </li>
                  <li>
                    · 언제든 해지할 수 있고, 해지해도 이미 결제한 이용기간이 끝날 때까지 이용할 수 있습니다.
                    이용기간 중 해지에 따른 일할 환불은 제공하지 않습니다.
                  </li>
                  <li>
                    · 결제에 실패하면 3일 동안 매일 다시 시도하며, 그때까지 결제되지 않으면 자동확인이 종료됩니다.
                  </li>
                  <li>
                    · 입력하신 계좌 인증 정보는 암호화하여 보관하며, 입금내역 조회 외의 목적으로 사용하지 않습니다.
                  </li>
                  <li>
                    · 카드번호 전체·유효기간·CVC는 저장하지 않습니다. 회사는 어느 카드인지 확인할 수 있도록
                    마스킹된 번호와 카드사명만 보관하고, 매달 결제는 결제사가 발급한 자동결제 등록정보로 요청합니다.
                  </li>
                </ul>
                <div className="mt-4 flex items-start gap-2.5 rounded-xl border border-[#dce0e7] bg-white p-3.5">
                  <input
                    id="auto-confirm-terms"
                    type="checkbox"
                    checked={form.agreedToTerms}
                    onChange={(e) => setForm({ ...form, agreedToTerms: e.target.checked })}
                    className="mt-0.5 h-4 w-4 accent-[#4285F4]"
                  />
                  <div className="text-[12px] font-bold text-[#303744]">
                    <label htmlFor="auto-confirm-terms" className="cursor-pointer">
                      위 내용과{" "}
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowTerms(true)}
                      className="font-bold text-[#4285F4] underline decoration-[#4285F4]/45 underline-offset-2 hover:text-[#3367D6]"
                    >
                      무통장입금 자동확인 서비스 이용약관
                    </button>
                    <label htmlFor="auto-confirm-terms" className="cursor-pointer">
                      에 동의합니다. <span className="text-[#f24a4a]">(필수)</span>
                    </label>
                  </div>
                </div>
              </section>
            </div>
          )}
        </div>

        <div className="flex gap-3 border-t border-[#e8eaf0] bg-[#fbfcfd] px-5 py-4 sm:px-7">
          <button
            onClick={
              stale ? () => void onCompleted() : step === 1 ? onClose : () => setStep(step - 1)
            }
            className="flex-1 rounded-lg border border-[#dce0e7] bg-white py-3 text-sm font-bold text-[#5a6272] sm:max-w-[180px]"
          >
            {stale ? "목록으로" : step === 1 ? "취소" : "이전"}
          </button>
          <button
            disabled={
              (step === 2 && !canNext) ||
              (step === 4 && !form.agreedToTerms) ||
              submitting ||
              stale
            }
            onClick={step < 4 ? () => setStep(step + 1) : submit}
            className="flex flex-[1.4] items-center justify-center gap-2 rounded-lg bg-[#4285F4] py-3 text-sm font-bold text-white hover:bg-[#3367D6] disabled:bg-[#b4bbcc]"
          >
            {submitting && <RefreshCw className="animate-spin" size={16} />}
            {step === 1
              ? `${bankLabel} 선택`
              : step === 2
                ? "계좌 정보 확인"
                : step === 3
                  ? "이 플랜으로 계속"
                  : `${money(amount)} 결제하고 시작`}
            {step < 4 && <ArrowRight size={16} />}
          </button>
        </div>
      </div>
      {showTerms && <AutoConfirmTermsModal onClose={() => setShowTerms(false)} />}
    </ModalShell>
  );
};


const PlanCard = ({
  selected,
  title,
  price,
  detail,
  badge,
  onClick,
}: {
  selected: boolean;
  title: string;
  price: string;
  detail: string;
  badge?: string;
  onClick: () => void;
}) => (
  <button
    onClick={onClick}
    className={`relative min-h-[220px] rounded-[12px] border p-5 text-left transition ${
      selected
        ? "border-[#4285F4] bg-[#F7FAFF] ring-2 ring-[#4285F4]/10"
        : "border-[#dfe3e9] bg-white hover:border-[#9EBEF4] hover:bg-[#FAFCFF]"
    }`}
  >
    <span
      className={`absolute left-5 top-5 flex h-5 w-5 items-center justify-center rounded-full border ${
        selected ? "border-[#4285F4] bg-[#4285F4]" : "border-[#cdd2db] bg-white"
      }`}
    >
      {selected && <span className="h-2 w-2 rounded-full bg-white" />}
    </span>
    {badge && (
      <span className="absolute right-4 top-4 inline-flex items-center gap-1 rounded-full bg-[#4285F4] px-2.5 py-1 text-[10px] font-bold text-white">
        <Sparkles size={10} /> {badge}
      </span>
    )}
    <div className="mt-8 text-base font-bold text-[#303644]">{title}</div>
    <div className="mt-4 text-[26px] font-bold tracking-[-0.6px] text-[#202532]">{price}</div>
    <div className="mt-2 text-xs font-semibold text-[#7d8494]">{detail}</div>
    <div className="mt-6 space-y-2 border-t border-[#e6e9f0] pt-4 text-[11px] font-semibold text-[#636c7e]">
      <div className="flex items-center gap-2"><Check size={13} className="text-emerald-500" /> 10분마다 자동 조회</div>
      <div className="flex items-center gap-2"><Check size={13} className="text-emerald-500" /> 계좌마다 따로 관리</div>
      <div className="flex items-center gap-2"><Check size={13} className="text-emerald-500" /> 언제든 해지 가능</div>
    </div>
  </button>
);

const SummaryRow = ({
  label,
  value,
  strong = false,
}: {
  label: string;
  value: string;
  strong?: boolean;
}) => (
  <div className="flex items-center justify-between border-b border-[#edf0f3] py-3 last:border-0">
    <span className="text-xs text-[#7b8393]">{label}</span>
    <span
      className={
        strong
          ? "text-lg font-bold text-[#4285F4]"
          : "text-xs font-bold text-[#303644]"
      }
    >
      {value}
    </span>
  </div>
);

